import { ButtonBase, Menu, MenuItem, styled } from '@mui/material'
import { MouseEvent, useState } from 'react'
import { ContentChip, ContentChipProps } from './ContentChip'
import { tokens } from 'ComponentLibrary/style-dictionary/output'
import { CaretDown } from '@phosphor-icons/react'

export type Option = {
    title: string
    callback: () => void
}

type ContentChipWithOptionsProps = {
    options: Option[]
    selectedOption?: string
} & Omit<ContentChipProps, 'highContrast' | 'size'>

export function ContentChipWithOptions({ options, selectedOption, ...contentChipProps }: ContentChipWithOptionsProps) {
    const [anchorEl, setAnchorEl] = useState<undefined | HTMLElement>()

    const onClick = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()
        setAnchorEl(event.currentTarget)
    }

    const onClose = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()
        setAnchorEl(undefined)
    }

    return (
        <>
            <ButtonBase onClick={onClick} disableRipple sx={{ gap: tokens.SizingSpacing075, alignItems: 'center' }}>
                <ContentChip {...contentChipProps} size="medium" highContrast />
                <CaretDown size={tokens.SizingIconS} weight="bold" color={tokens.ColorIconSubtle} />
            </ButtonBase>

            <Menu MenuListProps={{ sx: { py: tokens.SizingSpacing050 } }} anchorEl={anchorEl} open={!!anchorEl} onClose={onClose}>
                {options.map((option) => (
                    <StyledMenuItem
                        key={`menu-item-${option.title}`}
                        $selected={selectedOption === option.title}
                        onClick={(event) => {
                            event.stopPropagation()
                            option.callback()
                            setAnchorEl(undefined)
                        }}
                        disableRipple
                    >
                        {option.title}
                    </StyledMenuItem>
                ))}
            </Menu>
        </>
    )
}

const StyledMenuItem = styled(MenuItem, {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $selected?: boolean }>(({ $selected }) => ({
    ...tokens.TextM,
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    color: tokens.ColorTextBold,
    paddingLeft: tokens.SizingSpacing150,
    paddingRight: tokens.SizingSpacing150,
    padding: `0 ${tokens.SizingSpacing150}`,
    backgroundColor: $selected ? tokens.ColorSurfaceSelectedDefault : undefined,
}))
